import React, { useEffect, useMemo, useState } from "react";
import { DropdownMenuItemType, MessageBarType } from "@fluentui/react";
import { messageBarActions } from "../constants/messageBarConstants";
import DeleteSnippetDialog from "../dialogs/DeleteSnippetDialog";
import SaveSnippetDialog from "../dialogs/SaveSnippetDialog";
import UpdateSnippetDialog from "../dialogs/UpdateSnippetDialog";
import PreviewSnippetModal from "../modals/PreviewSnippetModal";
import { sortSnippets } from "../helpers/snippetHelpers";
import useAddSnippet from "../hooks/mutations/useAddSnippet";
import useDeleteSnippet from "../hooks/mutations/useDeleteSnippet";
import useUpdateSnippet from "../hooks/mutations/useUpdateSnippet";
import useCaseTypes from "../hooks/queries/useCaseTypes";
import useSnippets from "../hooks/queries/useSnippets";

import SnippetsTab from "./SnippetsTab";
import useUserRoles from "../hooks/queries/useUserRoles";
import { userRolesConstant } from "../constants/userRoleConstants";

const generalCaseType = [
  { key: 0, text: "General" },
  { key: "divider_1", text: "-", itemType: DropdownMenuItemType.Divider },
];

const SnippetsTabComponent = ({ caseId, caseTypeId, accountId = 0, accountName, onDisplayMessageBar }) => {
  const [selectedCaseType, setSelectedCaseType] = useState(null);
  const [selectedSnippet, setSelectedSnippet] = useState("");
  const [currentSnippet, setCurrentSnippet] = useState();

  const [isAddNewDialogOpen, setIsAddNewDialogOpen] = useState(false);
  const [isUpdateDialogOpen, setIsUpdateDialogOpen] = useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [isPreviewModalOpen, setIsPreviewModalOpen] = useState(false);

  const { data: caseTypes } = useCaseTypes(caseId);
  const { data: snippets } = useSnippets(selectedCaseType?.key, accountId);
  const addSnippet = useAddSnippet();
  const updateSnippet = useUpdateSnippet();
  const deleteSnippet = useDeleteSnippet();
  const { data: userRoles } = useUserRoles();

  const dropdownCaseTypes = useMemo(
    () =>
      caseTypes?.data?.map(({ caseTypeId: key, name: text, ...rest }) => ({
        key,
        text,
      })),
    [caseTypes?.data]
  );

  useEffect(() => {
    if (dropdownCaseTypes?.length) {
      if (caseTypeId) {
        const caseType = dropdownCaseTypes.find((caseType) => caseType.key === caseTypeId);
        setSelectedCaseType(caseType);
      } else {
        setSelectedCaseType(generalCaseType[0]);
      }
    }
  }, [caseTypeId, dropdownCaseTypes]);

  const handleCaseTypeChange = (type) => setSelectedCaseType(type);

  const getSnippet = (isNew, snippet) =>
    Office.context.document.getSelectedDataAsync(
      "html",
      {
        valueFormat: "unformatted",
      },
      (result) => {
        if (result.status === Office.AsyncResultStatus.Failed) {
          console.error(result.error);
        } else {
          const bodyHtml = /<body.*?>([\s\S]*)<\/body>/.exec(result.value)[1];
          setSelectedSnippet(bodyHtml);
          if (isNew) {
            return setIsAddNewDialogOpen(true);
          }
          setCurrentSnippet(snippet);
          return setIsUpdateDialogOpen(true);
        }
      }
    );

  const handleGetSelectedSnippet = (isNew = true, snippet = {}) =>
    Office.context.document.getSelectedDataAsync("text", (result) => {
      if (result.value === "") {
        return onDisplayMessageBar({
          isOpen: true,
          message: `Please select snippet to be ${isNew ? messageBarActions.ADD : messageBarActions.UPDATE}`,
          type: MessageBarType.warning,
        });
      } else {
        getSnippet(isNew, snippet);
      }
    });

  const handleInsertSnippet = (content) =>
    Word.run(async (context) => {
      const range = context.document.getSelection();
      range.insertHtml(content, Word.InsertLocation.end);
      await context.sync();
    });

  const handleDeleteSnippetClick = (snippet) => {
    setCurrentSnippet(snippet);
    setIsDeleteDialogOpen(true);
  };

  const handlePreviewSnippetClick = (snippet) => {
    setCurrentSnippet(snippet);
    setIsPreviewModalOpen(true);
  };

  const handleSaveSnippet = (caseTypeId, isAccountSnippet, isAdviserSnippet, description) => {
    const newSnippet = {
      content: selectedSnippet,
      caseTypeId: caseTypeId === 0 ? null : caseTypeId,
      accountId: isAccountSnippet ? accountId : null,
      isAdviserSnippet,
      description,
    };

    addSnippet.mutate(newSnippet, {
      onSuccess: () => {
        onDisplayMessageBar({
          isOpen: true,
          message: `${newSnippet.description} ${messageBarActions.ADD}!`,
          type: MessageBarType.success,
        });
      },
      onSettled: () => {
        setIsAddNewDialogOpen(false);
      },
    });
  };

  const handleUpdate = () => {
    updateSnippet.mutate(
      { id: currentSnippet?.key, content: selectedSnippet, caseTypeId: currentSnippet?.caseTypeId },
      {
        onSuccess: () => {
          onDisplayMessageBar({
            isOpen: true,
            message: `${currentSnippet.text} ${messageBarActions.UPDATE}!`,
            type: MessageBarType.success,
          });
        },
        onSettled: () => {
          setIsUpdateDialogOpen(false);
        },
      }
    );
  };

  const handleDelete = () => {
    deleteSnippet.mutate(
      { id: currentSnippet?.key, caseTypeId: currentSnippet?.caseTypeId },
      {
        onSuccess: () => {
          onDisplayMessageBar({
            isOpen: true,
            message: `${currentSnippet.text} ${messageBarActions.DELETE}!`,
            type: MessageBarType.success,
          });
        },
        onSettled: () => {
          setIsDeleteDialogOpen(false);
        },
      }
    );
  };

  const hasGlobalPermission = (userRolesList) => {
    const globalPermission = userRolesList?.some(
      (role) => role.value === userRolesConstant.LEGAL_ADMIN || role.value === userRolesConstant.SUPER_USER
    );

    return !!globalPermission;
  };

  return (
    <>
      <SnippetsTab
        snippets={sortSnippets(snippets?.data, selectedCaseType?.key)}
        caseAccountId={accountId}
        selectedCaseType={selectedCaseType}
        accountName={accountName}
        caseTypes={dropdownCaseTypes?.length ? [...generalCaseType, ...dropdownCaseTypes] : [{}]}
        onCaseTypeChange={handleCaseTypeChange}
        onInsertSnippetClick={handleInsertSnippet}
        onUpdateSnippet={handleGetSelectedSnippet}
        onDeleteSnippet={handleDeleteSnippetClick}
        onPreviewSnippet={handlePreviewSnippetClick}
        onNewSnippetClick={handleGetSelectedSnippet}
        hasAccountAccess={!!accountId}
        canManageGlobal={hasGlobalPermission(userRoles?.data)}
      />

      <SaveSnippetDialog
        open={isAddNewDialogOpen}
        handleOpen={setIsAddNewDialogOpen}
        onSave={handleSaveSnippet}
        caseTypes={dropdownCaseTypes?.length ? [...generalCaseType, ...dropdownCaseTypes] : [{}]}
        canManageGlobal={hasGlobalPermission(userRoles?.data)}
        hasAccountAccess={!!accountId}
      />

      <UpdateSnippetDialog
        open={isUpdateDialogOpen}
        handleOpen={setIsUpdateDialogOpen}
        onUpdate={handleUpdate}
        snippetDescription={currentSnippet?.text}
      />

      <DeleteSnippetDialog
        open={isDeleteDialogOpen}
        handleOpen={setIsDeleteDialogOpen}
        onDelete={handleDelete}
        snippetDescription={currentSnippet?.text}
      />

      <PreviewSnippetModal snippet={currentSnippet} isOpen={isPreviewModalOpen} onClose={setIsPreviewModalOpen} />
    </>
  );
};

export default SnippetsTabComponent;
